/**
 * The hand-off between the prospect page and the page Checkout returns to (`/signin/paid`). The prospect page keeps
 * the email the person paid with, and the prospect's slug, in this tab's sessionStorage (same origin as the app, and
 * gone when the tab closes). Nothing goes in a URL.
 */
import { ApiError, call } from './api.ts';

export const PAID_EMAIL_KEY = 'ff-paid-email';
export const PAID_SLUG_KEY = 'ff-paid-slug';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Wait until the account exists. Stripe's webhook makes it, and it can land a second or two after the person's
 * browser does; asking for a sign in link before then would send nothing. The prospect turns from open to closed once
 * the account is made (`eligible` goes false), so poll that. Gives up quietly after `tries` and lets the caller
 * carry on: the page's "send a new code" button covers the rest.
 */
export async function accountReady(
  slug: string,
  opts: { tries?: number; gapMs?: number; check?: (slug: string) => Promise<boolean>; wait?: (ms: number) => Promise<void> } = {},
): Promise<boolean> {
  const { tries = 10, gapMs = 1000, check = signedUp, wait = sleep } = opts;
  for (let i = 0; i < tries; i++) {
    if (await check(slug)) return true;
    if (i < tries - 1) await wait(gapMs);
  }
  return false;
}

async function signedUp(slug: string): Promise<boolean> {
  try {
    const p = await call('prospectPage', { params: { slug } });
    return !p.eligible;
  } catch (e) {
    // Gone or withdrawn means it isn't an open prospect any more: nothing left to wait for.
    return e instanceof ApiError && (e.code === 'gone' || e.code === 'not_found');
  }
}
